import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export interface MeResponse {
  user: {
    id: number;
    email: string;
    name: string;
    accountId: number | null;
    picture: string | null;
  };
}

/** Returns the current user, or throws AuthError if not logged in. */
export class AuthError extends Error {
  constructor() {
    super('Not authenticated');
    this.name = 'AuthError';
  }
}

export function useMe() {
  return useQuery<MeResponse>({
    queryKey: ['me'],
    queryFn: async () => {
      const res = await fetch('/api/me', { credentials: 'include' });
      if (res.status === 401) throw new AuthError();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    retry: (_failures, err) => !(err instanceof AuthError),
    staleTime: 60_000,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
    },
    onSettled: () => {
      qc.clear();
      // Bounce to login (which will re-bounce to /auth/login → Accounts)
      window.location.href = '/';
    },
  });
}

export function goToLogin(): void {
  // Round-trip to API so cookies + redirects are server-driven
  window.location.href = '/auth/login';
}
